/**
 * Marketer-mirror model (SKE-4166, phase 2).
 *
 * The demo's payoff is showing BOTH sides of an approval at once: the visitor
 * plays the client in the real approver UI, and a compact mock of Sked — the
 * marketer's side — reacts to every action they take.
 *
 * Everything the mirror renders is derived here, as pure functions over the
 * visitor's demo store. That is deliberate and load-bearing:
 *
 *   - there is exactly ONE source of truth (the store the approver UI already
 *     writes), so the two sides can never disagree,
 *   - no network call and no polling is involved — the mirror re-renders
 *     because the store published a change (see `subscribeDemoStore`),
 *   - the derivation is testable without React.
 *
 * Nothing in this file reads `window`, so it stays a pure model.
 */

import type { DemoAction, DemoStore } from './demoStore'
import { demoFormat, demoStrings } from './demoStrings'
import type { DemoRawPost, DemoStatusType } from './demoTypes'

/** How much of a caption the mirror shows before eliding. */
export const CAPTION_SNIPPET_LENGTH = 42

/** One row of the mock Sked panel. */
export interface MirrorPost {
  id: string
  /** The client account the post publishes to — "Marigold & Rye" etc. */
  brand: string
  avatar?: string
  platform?: string
  thumbnail?: string
  caption: string
  /** Caption trimmed to a single readable line. */
  snippet: string
  when?: string
  status: DemoStatusType
  statusLabel: string
}

/** A "your client just did something" line — rendered as a toast and in the feed. */
export interface MirrorNotice {
  /** Stable across renders; also the React key and the de-dup identity. */
  key: string
  postId: string
  status: DemoStatusType
  kind: 'status' | 'comment'
  brand: string
  snippet: string
  /** The formatted, already-localised sentence. */
  message: string
  at: string
}

export interface MirrorProgress {
  total: number
  reviewed: number
  pending: number
  approved: number
  requestChange: number
}

/* -------------------------------------------------------------------------- */
/* Field access                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `DemoRawPost` is deliberately loose (`[key: string]: unknown`) because it
 * mirrors the wire payload verbatim. These readers narrow just the handful of
 * fields the mirror needs, and never throw on a shape they don't recognise —
 * a missing avatar must not take the demo down.
 */
interface RawAccount {
  name?: unknown
  avatar?: unknown
  platform?: unknown
}

const firstAccount = (post: DemoRawPost): RawAccount | undefined => {
  const accounts = post.accounts
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return undefined
  }
  const [account] = accounts as unknown[]
  return typeof account === 'object' && account !== null
    ? (account as RawAccount)
    : undefined
}

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const thumbnailOf = (post: DemoRawPost): string | undefined => {
  const media = post.media
  if (typeof media !== 'object' || media === null) {
    return undefined
  }
  return asString((media as { src?: unknown }).src)
}

/* -------------------------------------------------------------------------- */
/* Derivation                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Trim a caption to one line. Breaks on a word boundary where there is one, so
 * the toast never cuts mid-word.
 */
export const captionSnippet = (
  caption: string | undefined,
  length = CAPTION_SNIPPET_LENGTH
): string => {
  const collapsed = (caption ?? '').replace(/\s+/g, ' ').trim()

  if (collapsed.length <= length) {
    return collapsed
  }

  const cut = collapsed.slice(0, length)
  const lastSpace = cut.lastIndexOf(' ')

  return `${(lastSpace > length / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

const STATUS_LABEL_KEYS = {
  pending: 'mirrorStatusPending',
  approved: 'mirrorStatusApproved',
  requestChange: 'mirrorStatusRequestChange',
} as const

export const mirrorStatusLabel = (status: DemoStatusType): string =>
  demoStrings[STATUS_LABEL_KEYS[status]]

const NOTICE_KEYS = {
  pending: 'mirrorNoticePending',
  approved: 'mirrorNoticeApproved',
  requestChange: 'mirrorNoticeRequestChange',
} as const

/** Map one stored post onto a mirror row. */
export const toMirrorPost = (post: DemoRawPost): MirrorPost => {
  const account = firstAccount(post)

  return {
    id: post.id,
    brand: asString(account?.name) ?? demoStrings.mirrorWorkspaceName,
    avatar: asString(account?.avatar),
    platform: asString(account?.platform),
    thumbnail: thumbnailOf(post),
    caption: post.caption ?? '',
    snippet: captionSnippet(post.caption),
    when: post.when ?? undefined,
    status: post.status.type,
    statusLabel: mirrorStatusLabel(post.status.type),
  }
}

const scheduledAt = (post: MirrorPost): number => {
  const time = post.when ? Date.parse(post.when) : Number.NaN
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

/**
 * The mock Sked panel lists the same posts the approver sees, soonest first —
 * a marketer's calendar order, not the approval queue's selection order.
 */
export const toMirrorPosts = (store: DemoStore | null): MirrorPost[] =>
  (store?.posts ?? [])
    .map(toMirrorPost)
    .sort((a, b) => scheduledAt(a) - scheduledAt(b))

export const mirrorProgress = (posts: MirrorPost[]): MirrorProgress => {
  const count = (status: DemoStatusType) =>
    posts.filter((post) => post.status === status).length

  const pending = count('pending')

  return {
    total: posts.length,
    reviewed: posts.length - pending,
    pending,
    approved: count('approved'),
    requestChange: count('requestChange'),
  }
}

/**
 * Turn one entry of the store's append-only action trail into the line the
 * marketer would actually see in Sked.
 *
 * Returns null when the action points at a post that is no longer in the
 * snapshot (a reset mid-flight), because a notice with no post behind it has
 * nothing useful to say.
 */
export const toMirrorNotice = (
  action: DemoAction,
  posts: MirrorPost[]
): MirrorNotice | null => {
  const post = posts.find((item) => item.id === action.postId)

  if (!post) {
    return null
  }

  const kind = action.kind ?? 'status'
  const messageKey =
    kind === 'comment' ? 'mirrorNoticeComment' : NOTICE_KEYS[action.status]

  return {
    // postId + timestamp + kind is unique per action: the trail is append-only
    // and two entries for one post can't share a millisecond AND a kind.
    key: `${action.postId}:${action.at}:${kind}`,
    postId: action.postId,
    status: action.status,
    kind,
    brand: post.brand,
    snippet: post.snippet,
    message: demoFormat(messageKey, {
      brand: post.brand,
      caption: post.snippet,
    }),
    at: action.at,
  }
}

/**
 * The notices for everything that happened after `seenCount` entries of the
 * trail.
 *
 * Callers hold `seenCount` so that actions already in the store when the page
 * loaded (a returning visitor's own history) don't fire a burst of toasts on
 * mount — only what the visitor does in front of us animates.
 */
export const noticesSince = (
  store: DemoStore | null,
  seenCount: number,
  posts: MirrorPost[]
): MirrorNotice[] => {
  const actions = store?.actions ?? []

  if (seenCount >= actions.length) {
    return []
  }

  return actions
    .slice(seenCount)
    .map((action) => toMirrorNotice(action, posts))
    .filter((notice): notice is MirrorNotice => notice !== null)
}

/**
 * The most recent actions, newest first — the marketer's activity feed. Unlike
 * `noticesSince` this deliberately includes history from before mount: the feed
 * is a record, not an alert.
 */
export const recentNotices = (
  store: DemoStore | null,
  posts: MirrorPost[],
  limit = 4
): MirrorNotice[] =>
  (store?.actions ?? [])
    .slice(-limit)
    .reverse()
    .map((action) => toMirrorNotice(action, posts))
    .filter((notice): notice is MirrorNotice => notice !== null)
